import { GET_HAND_HISTORY, GOT_HAND_HISTORY } from 'constants/eventConstants';
import { HandHistoryResponse, HandHistorySnapshot } from 'components/HandHistory/types';

export interface HandGrant {
  tableId: string; gameVariant: string; gameNo: number; handId: string;
  completedAt: number; expiresAt: number; key: string; seat: number;
}

export const requestHandGrants = (socket: any, request: Record<string, unknown>): Promise<HandGrant[]> =>
  new Promise((resolve, reject) => {
    if (!socket) { reject(new Error('History connection unavailable')); return; }
    const cleanup = () => { clearTimeout(timer); socket.off(GOT_HAND_HISTORY, receive); };
    const receive = (payload: any) => {
      if (payload?.requestId !== request.requestId || !payload?.accessOnly) return;
      cleanup();
      if (!payload.success || !Array.isArray(payload.data?.grants)) reject(new Error('History access unavailable'));
      else resolve(payload.data.grants);
    };
    const timer = setTimeout(() => { cleanup(); reject(new Error('History access timed out')); }, 8000);
    socket.on(GOT_HAND_HISTORY, receive);
    socket.emit(GET_HAND_HISTORY, { ...request, accessOnly: true });
  });

const bytes = (value: string) => Uint8Array.from(atob(value), (char) => char.charCodeAt(0));

export const decryptHistoryDocument = async (
  document: any, grants: HandGrant[], tableId: string, gameVariant: string, sessionStartedAt: number,
  firstGameNo?: number
): Promise<HandHistoryResponse> => {
  // A known first played hand bounds the session by game number; comparing the
  // device-clock sessionStartedAt with server completion times is skew-prone.
  const beforeSession = (hand: any) =>
    firstGameNo ? Number(hand.gameNo) < firstGameNo : hand.completedAt < sessionStartedAt;
  if (document?.schemaVersion !== 2 || String(document.tableId) !== tableId
    || document.gameVariant !== gameVariant || !Array.isArray(document.completedHands)
    || document.completedHands.length > 200) throw new Error('Invalid encrypted history');
  const hands = await Promise.all(document.completedHands.map(async (hand: any) => {
    const grant = grants.find((entry) => entry.gameNo === hand.gameNo && entry.handId === hand.handId
      && entry.completedAt === hand.completedAt && entry.expiresAt === hand.expiresAt
      && entry.tableId === tableId && entry.gameVariant === gameVariant);
    if (!grant || grant.expiresAt <= Date.now() || beforeSession(hand)) return null;
    if (hand.algorithm !== 'A256GCM' || String(hand.tableId) !== tableId || hand.gameVariant !== gameVariant)
      throw new Error('Invalid encrypted hand identity');
    const key = await crypto.subtle.importKey('raw', bytes(grant.key), 'AES-GCM', false, ['decrypt']);
    const plaintext = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: bytes(hand.iv),
      additionalData: new TextEncoder().encode(JSON.stringify([2, gameVariant, tableId,
        hand.gameNo, hand.handId, hand.completedAt, hand.expiresAt])), tagLength: 128
    }, key, bytes(hand.ciphertext));
    const snapshot: HandHistorySnapshot = JSON.parse(new TextDecoder().decode(plaintext));
    if (snapshot.handId !== hand.handId || snapshot.gameNo !== hand.gameNo || snapshot.completedAt !== hand.completedAt
      || String(snapshot.tableId) !== tableId || snapshot.gameVariant !== gameVariant)
      throw new Error('Decrypted hand identity mismatch');
    return snapshot;
  }));
  const completedHands = hands.filter((hand): hand is HandHistorySnapshot => hand !== null);
  return { locked: completedHands.length === 0, completedHands, storageSource: 'cdn-json' };
};
